/**
 *
 * App
 *
 */
import React, { useEffect, useContext } from 'react';
import { hot } from 'react-hot-loader';
import { Route, Switch, matchPath } from 'react-router-dom';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { makeSelectAppState, makeSelectIsAppDetailsLoading } from './selectors';
import reducer from './reducer';
import saga from './saga';
import routeTemplates from 'common/route-templates';
import Chat from 'ui/containers/Chat';
import AuthenticatedRoute from 'ui/containers/AuthenticatedRoute';
import Notifier from 'ui/containers/Notifier';
import RegisterPage from 'ui/containers/RegisterPage/Loadable';
import LoginPage from 'ui/containers/LoginPage/Loadable';
import ForgotPassword from 'ui/containers/ForgotPassword/Loadable';
import EmailVerify from 'ui/containers/EmailVerify/Loadable';
import OauthRedirect from 'ui/containers/OauthRedirect/Loadable';
import Invite from 'ui/containers/Invite/Loadable';
import ProjectDetailsPage from 'ui/containers/ProjectDetailsPage/Loadable';
import SlackVerify from '../SlackVerify';
import HubspotVerify from '../HubspotVerify';
import GoogleVerify from '../GoogleVerify';
import MicrosoftVerify from '../MicrosoftVerify';
import MsTeamsVerify from '../MsTeamsVerify';
import SalesforceVerify from '../SalesforceVerify';
import PardotVerify from '../PardotVerify';
import ZendeskVerify from '../ZendeskVerify';
import EloquaVerify from '../EloquaVerify';
import ZohoVerify from '../ZohoVerify';
import MsDynamicsVerify from '../MsDynamicsVerify';
import Iframe from '../Iframe';
import TeamsSuccessPage from './TeamsSuccessPage';
import localStorageTemplates from 'common/local-storage-templates';
import Notification from 'ui/containers/Notification';
import queryString from 'query-string';
import { AuthContext } from 'ui/containers/Providers/AuthProvider';
import { getTeamsOnlyToken, getAppDetails } from './actions';
import { isCsDashboard } from 'ui/containers/CsDashboard/utils';
import PageLoader from 'ui/components/Loaders/PageLoader';
import { useModal } from 'react-modal-hook';
import UpgradeConfirmationModal from 'ui/components/UpgradeConfirmationModal';
import { Helmet } from 'react-helmet';
import { useConfig } from 'config.js';

export const App = props => {
  const { history, getTeamsOnlyTokenStart, getAppDetailsStart, isAppDetailsLoading } = props;
  useInjectReducer({ key: 'global', reducer });
  useInjectSaga({ key: 'global', saga });

  const { config, loading: configLoading } = useConfig();
  const { REACT_APP_INTEGRATIONS_ANALYTICS_NAV: REACT_APP_CS_DASHBOARD_VALID_URLS } = config || {};
  const { authenticated, setAuthenticated } = useContext(AuthContext);
  const searchParams = queryString.parse(window.location.search);
  const redirectUrl = searchParams.redirectUrl;
  const { subEntityId } = searchParams;
  let conversationId = null;
  try {
    conversationId = subEntityId && JSON.parse(subEntityId).channelId;
  } catch (e) {}
  const isChatOnly = searchParams['chat_only'] && searchParams['chat_only'] === 'true';
  const isChangePassword = matchPath(props.location.pathname, {
    path: routeTemplates.passwordChange,
  });

  const [showUpgradeConfirmationModal, hideUpgradeConfirmationModal] = useModal(
    ({ in: open, onExited }) => (
      <UpgradeConfirmationModal
        open={open}
        hideModal={hideUpgradeConfirmationModal}
        onExited={onExited}
      />
    ),
    []
  );

  window.showEmailConfirmationPopup = showUpgradeConfirmationModal;

  useEffect(() => {
    localStorage.removeItem(localStorageTemplates.insentNetworkStatus);
    if (isChatOnly && !authenticated) {
      const { tenant: tenantId, group: teamId, channelId, userObjectId } = searchParams;
      if (tenantId && teamId && channelId && userObjectId) {
        getTeamsOnlyTokenStart({
          metadata: {
            payload: {
              tenantId,
              teamId,
              channelId,
              userObjectId,
            },
            conversationId,
            setAuthenticated,
          },
        });
      }
    } else {
      getAppDetailsStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAppDetailsLoading && !configLoading) {
      let isInbox = matchPath(props.location.pathname, {
        path: routeTemplates.project.inbox.root,
      });
      if (!isInbox || !isInbox.isExact) {
        isInbox = matchPath(props.location.pathname, {
          path: routeTemplates.project.inbox.detail,
        });
      }
      if (redirectUrl && authenticated) {
        history.push(decodeURIComponent(redirectUrl));
      } else if (props.location.pathname === '/' && authenticated) {
        if (isCsDashboard(REACT_APP_CS_DASHBOARD_VALID_URLS)) {
          history.push(routeTemplates.project.cs.customerRecords);
        } else if (
          window.appDetails.isDoziCustomer &&
          !window.appDetails.migrationInProgress &&
          !window.appDetails.isInsideMSTeams &&
          !window.appDetails.user.isCSUser
        ) {
          history.push(routeTemplates.project.audiences.root);
        } else {
          history.push(routeTemplates.project.inbox.detail);
        }
      } else if (props.location.pathname === routeTemplates.iframe) {
        history.push(props.location.pathname + props.location.search);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.pathname, isAppDetailsLoading, configLoading, REACT_APP_CS_DASHBOARD_VALID_URLS]);
  return (
    <>
      <Helmet>
        <link rel="icon" href={`/assets/img/sales_logo.png`} type="image/png" sizes="16x16" />
      </Helmet>
      <Notification />
      <Notifier />
      {authenticated &&
      (isAppDetailsLoading || configLoading) &&
      !matchPath(props.location.pathname, {
        path: routeTemplates.passwordChange,
      }) ? (
        <div>
          <PageLoader />
        </div>
      ) : (
        <Switch>
          <Route exact path={routeTemplates.register} component={RegisterPage} />
          <Route exact path={routeTemplates.iframe} component={Iframe} />
          <Route exact path={routeTemplates.teamsSuccess} component={TeamsSuccessPage} />
          <Route exact path={routeTemplates.login} component={LoginPage} />
          <Route exact path={routeTemplates.passwordReset} component={ForgotPassword} />
          <Route exact path={routeTemplates.emailVerify} component={EmailVerify} />
          <Route exact path={routeTemplates.invite} component={Invite} />
          <Route exact path={routeTemplates.integration.verify.slack} component={SlackVerify} />
          <Route exact path={routeTemplates.integration.verify.hubspot} component={HubspotVerify} />
          <Route
            exact
            path={routeTemplates.integration.verify.microsoft}
            component={MicrosoftVerify}
          />
          <Route exact path={routeTemplates.integration.verify.google} component={GoogleVerify} />
          <Route exact path={routeTemplates.integration.verify.msteams} component={MsTeamsVerify} />
          <Route exact path={routeTemplates.integration.verify.zendesk} component={ZendeskVerify} />
          <Route exact path={routeTemplates.integration.verify.eloqua} component={EloquaVerify} />
          <Route exact path={routeTemplates.integration.verify.zoho} component={ZohoVerify} />
          <Route exact path={routeTemplates.integration.redirect} component={OauthRedirect} />
          <Route
            exact
            path={routeTemplates.integration.verify.salesforce}
            component={SalesforceVerify}
          />
          <Route exact path={routeTemplates.integration.verify.pardot} component={PardotVerify} />
          <Route
            exact
            path={routeTemplates.integration.verify.msDynamics}
            component={MsDynamicsVerify}
          />
          {isChangePassword ? (
            <AuthenticatedRoute
              exact
              path={routeTemplates.passwordChange}
              component={ForgotPassword}
            />
          ) : !isChatOnly ? (
            <AuthenticatedRoute path={routeTemplates.home} component={ProjectDetailsPage} />
          ) : (
            ''
          )}
          <AuthenticatedRoute exact path={routeTemplates.project.inbox.root} component={Chat} />
          <AuthenticatedRoute exact path={routeTemplates.project.inbox.detail} component={Chat} />
        </Switch>
      )}
    </>
  );
};

App.propTypes = {};

export const mapStateToProps = (state, props) => {
  return createStructuredSelector({
    app: makeSelectAppState(),
    isAppDetailsLoading: makeSelectIsAppDetailsLoading(),
  });
};

export const mapDispatchToProps = dispatch => {
  return {
    getTeamsOnlyTokenStart: ({ metadata }) => dispatch(getTeamsOnlyToken.start({ metadata })),
    getAppDetailsStart: () => dispatch(getAppDetails.start()),
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default hot(module)(compose(withRouter, withConnect)(App));
